import { type IntlShape, useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { getProductForTranslation } from '../services/get-product-translation-key';
import { isEligibleForStorageHardLimit } from '../services/is-eligible-for-storage-hard-limit';
import type { Product } from '../types';

import { type EditionAwarenessContextType, useEditionAwareness } from './edition-awareness-context';
import { useGetExperimentValue } from './experiment-context';
import { isEligibleForConfluenceBuyReverseTrialExperiment } from './is-eligible-for-confluence-buy-reverse-trials';
import { isEligibleForConfluenceEndUserRequestExperiment } from './is-eligible-for-confluence-end-user-request';
import { isEligibleForManualFeatureOnboarding } from './is-eligible-for-manual-feature-onboarding';
import { isEligibleForServCoUplift } from './is-eligible-for-service-collection-uplift';
import { isEligibleForStandardToPremium } from './is-eligible-for-standard-to-premium';
import { isProductOneOf } from './is-product-one-of';

// TODO: `licensedUserLimit` should be fetched from the server
const PRODUCT_LIMITS_MAP: Record<Product, { licensedUserLimit: number; warningUserLimit: number }> =
	{
		'jira-product-discovery': {
			licensedUserLimit: 3,
			warningUserLimit: 2,
		},
		jira: {
			licensedUserLimit: 10,
			warningUserLimit: 7,
		},
		confluence: {
			licensedUserLimit: 10,
			warningUserLimit: 3,
		},
		'jira-service-management': {
			licensedUserLimit: 3,
			warningUserLimit: 2,
		},
	};

export type IconType = 'gem' | 'warning' | 'credit-card';
export type Appearance = 'default' | 'warning' | 'danger';

export type ButtonContent = {
	appearance: Appearance;
	icon: IconType;
	isDismissible?: boolean;
	isEducationDiscount?: boolean;
	text: string;
};

function getFreeContent({
	context,
	formatMessage,
	getExperimentValue,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
	getExperimentValue: ReturnType<typeof useGetExperimentValue>['getExperimentValue'];
}): ButtonContent {
	const { licensedUsers, product, automationFreeLimitStatus } = context;
	const limits = PRODUCT_LIMITS_MAP[product];

	const upgradeText = formatMessage({
		id: '@atlassian/edition-awareness.button.upgrade',
		defaultMessage: 'Upgrade',
		description: 'Informs the user that they can upgrade their product',
	});

	// Put at the top because we are expanding the audience and we need to be sure this check is done first
	if (fg('connie_end_user_request_kill_switch')) {
		if (isEligibleForConfluenceEndUserRequestExperiment(context)) {
			return {
				text: upgradeText,
				appearance: 'default',
				icon: 'gem',
			};
		}
	}

	// Is eligible for automation limits
	if (fg('growth_automation_limits_kill_switch')) {
		if (automationFreeLimitStatus) {
			if (
				automationFreeLimitStatus === 'BREACHED' &&
				getExperimentValue({
					statsigParams: {
						experimentName: 'growth_automation_limits_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
				}) === 'variation'
			) {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.automation-limits-breached',
						defaultMessage: 'Limit reached',
						description: 'Informs the user that they have breached their automation limits',
					}),
					appearance: 'danger',
					icon: 'warning',
				};
			}
			if (
				automationFreeLimitStatus === 'PROJECTED_TO_BREACH' &&
				getExperimentValue({
					statsigParams: {
						experimentName: 'growth_automation_limits_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
				}) === 'variation'
			) {
				// If the automation usage is projected to breach, we show a warning
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.automation-near-limit',
						defaultMessage: 'Near limit',
						description: 'Informs the user that they are about to breach their automation limits',
					}),
					appearance: 'warning',
					icon: 'warning',
				};
			}
		}
	}

	if (fg('jira-storage-hard-limit-gate')) {
		if (
			isEligibleForStorageHardLimit(context) &&
			getExperimentValue({
				statsigParams: {
					experimentName: 'jira-storage-hard-limit',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.storage-hard-limit',
					defaultMessage: 'Storage full',
				}),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	if (isProductOneOf(product, ['jira-product-discovery', 'jira-service-management'])) {
		if (
			licensedUsers >= limits.licensedUserLimit &&
			getExperimentValue({
				statsigParams: {
					experimentName: 'jsm_jpd_ea_free_content_adjustment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.no-users-left-content-adjustment',
						defaultMessage: 'No {product, select, jpd {creators} jsm {agents} other {users}} left',
						description:
							'Informs the user that there are no licensed users left and they may need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: 'warning',
				icon: 'warning',
			};
		}

		if (
			limits.licensedUserLimit - licensedUsers === 1 &&
			getExperimentValue({
				statsigParams: {
					experimentName: 'jsm_jpd_ea_free_content_adjustment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.limited-users-left-content-adjustment',
						defaultMessage: '1 {product, select, jpd {creator} jsm {agent} other {user}} left',
						description:
							'Informs the user that there is 1 licensed user left before they need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: 'default',
				icon: 'gem',
			};
		}
	}

	if (isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery'])) {
		if (licensedUsers >= limits.licensedUserLimit) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.no-users-left',
						defaultMessage:
							'No {product, select, jpd {creators} jsm {agents} jira {users} confluence {users} other {users}} left',
						description:
							'Informs the user that there are no licensed users left and they may need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: product !== 'confluence' ? 'danger' : 'warning',
				icon: 'warning',
			};
		}

		if (licensedUsers >= limits.warningUserLimit) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.limited-users-left',
						defaultMessage:
							'Limited {product, select, jpd {creators} jsm {agents} jira {users} confluence {users} other {users}} left',
						description:
							'Informs the user that there are limited licensed users left before they need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	if (product === 'confluence' && licensedUsers >= limits.warningUserLimit) {
		// Confluence has its own variety when compared to the `userLimits` feature
		return {
			text: upgradeText,
			appearance: 'warning',
			icon: 'warning',
		};
	}

	return {
		text: upgradeText,
		appearance: 'default',
		icon: 'gem',
	};
}

function getTrialContent({
	context,
	formatMessage,
	getExperimentValue,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
	getExperimentValue: ReturnType<typeof useGetExperimentValue>['getExperimentValue'];
}): ButtonContent {
	const { product, edition, trialDaysLeft, trialLengthDays, isInReverseTrial, userRoles } = context;

	let appearance: ButtonContent['appearance'] = 'default';
	let icon: ButtonContent['icon'] = 'gem';

	const daysLeftText = formatMessage(
		{
			id: '@atlassian/edition-awareness.button.daysLeft',
			defaultMessage: '{trialDaysLeft, plural, one {# day} other {# days}} left',
			description: 'Informs the user how many days they have left in trial',
		},
		{ trialDaysLeft },
	);

	const isInFirstHalfOfTrial = Boolean(
		trialDaysLeft && trialLengthDays && trialDaysLeft > Math.ceil(trialLengthDays / 2),
	);

	if (
		edition === 'premium' &&
		product === 'jira' &&
		isInReverseTrial &&
		userRoles?.includes('BILLING_ADMIN') &&
		fg('jira_reverse_trial_ea_updates_during_trial_gate')
	) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jira_reverse_trial_ea_updates_during_trial',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			appearance = 'default';
			icon = 'gem';

			if (isInFirstHalfOfTrial) {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.see-plans',
						defaultMessage: 'See plans',
						description: 'Informs the user that they are able to see plans',
					}),
					appearance,
					icon,
				};
			}

			if (trialDaysLeft && trialDaysLeft <= 3) {
				appearance = 'warning';
				icon = 'warning';
			}

			return {
				text: daysLeftText,
				appearance,
				icon,
			};
		}
	}

	if (isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery'])) {
		// If trial is still in its first half, show trial message instead of trial days left
		if (isInFirstHalfOfTrial) {
			if (edition === 'premium') {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.premium-trial',
						defaultMessage: 'Premium trial',
						description: 'Informs the user that they are on a premium trial',
					}),
					appearance,
					icon,
				};
			}

			if (edition === 'standard') {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.standard-trial',
						defaultMessage: 'Standard trial',
						description: 'Informs the user that they are on a standard trial',
					}),
					appearance,
					icon,
				};
			}
		}

		// If trial has 3 days or less remaining, swap the button to the warning state
		if (trialDaysLeft && trialDaysLeft <= 3) {
			appearance = 'warning';
			icon = 'warning';
		}

		return {
			text: daysLeftText,
			appearance,
			icon,
		};
	}

	if (
		isEligibleForManualFeatureOnboarding(context) &&
		getExperimentValue({
			statsigParams: {
				experimentName: 'manual_trial_w_pde_onboarding',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
		}) === 'variation'
	) {
		return {
			text: formatMessage(
				{
					id: '@atlassian/edition-awareness.button.explore-edition',
					defaultMessage:
						'Explore {edition, select, premium {Premium} standard {Standard} other {Trial}}',
					description: 'CTA for the user to explore the features of their trial',
				},
				{ edition },
			),

			appearance: 'default',
			icon: 'gem',
		};
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment(context) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const abCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'growth_confluence_buy_reverse_trials_ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'luna',
				trackEventAttributes: {
					touchpointId: 'use-button-content getTrialContent',
				},
			},
		});

		if (abCohort === 'variation') {
			appearance = 'default';
			icon = 'gem';

			if (trialDaysLeft && trialDaysLeft > 15) {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.see-plans.cbrt',
						defaultMessage: 'See plans',
						description: 'Informs the user that they are able to see plans',
					}),
					appearance,
					icon,
				};
			}

			if (trialDaysLeft && trialDaysLeft <= 3) {
				appearance = 'warning';
				icon = 'warning';
			}

			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.see-plans.cbrt.final',
						// eslint-disable-next-line @atlassian/i18n/enforce-plural-rules
						defaultMessage: '{trialDaysLeft, plural, =1 {# day} other {# days}} left',
						description: 'Indicates to the user how many days they have left in trial',
					},
					{ trialDaysLeft },
				),
				appearance,
				icon,
			};
		}
	}

	return {
		text: daysLeftText,
		appearance,
		icon,
	};
}

function getPredunningContent({
	context,
	formatMessage,
	getExperimentValue,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
	getExperimentValue: ReturnType<typeof useGetExperimentValue>['getExperimentValue'];
}): ButtonContent {
	const { edition, product, isInReverseTrial, userRoles } = context;

	let appearance: ButtonContent['appearance'] = 'default';
	let icon: ButtonContent['icon'] = 'credit-card';

	if (
		product === 'jira' &&
		edition === 'premium' &&
		isInReverseTrial &&
		userRoles?.includes('BILLING_ADMIN') &&
		fg('jira_reverse_trial_ea_updates_during_trial_gate')
	) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jira_reverse_trial_ea_updates_during_trial',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.choose-plans',
					defaultMessage: 'Choose a plan',
					description: 'Informs the user that they are able to choose a plan',
				}),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment(context) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const abCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'growth_confluence_buy_reverse_trials_ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'luna',
				trackEventAttributes: {
					touchpointId: 'use-button-content getPredunningContent',
				},
			},
		});

		if (abCohort === 'variation') {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.see-plans.final',
					defaultMessage: 'Choose a plan',
					description: 'Informs the user that they are able to choose a plan',
				}),

				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	return {
		text: formatMessage({
			id: '@atlassian/edition-awareness.button.addPaymentDetails',
			defaultMessage: 'Add payment details',
			description: 'Informs the user how many days they have left in trial',
		}),
		appearance,
		icon,
	};
}

function getCollectionContent({
	context,
	formatMessage,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
}): ButtonContent {
	/* ==============================
	// Collection
	// Collection handles its own logic for the button content
	// This is to avoid the button content from being overridden by other products' logic
	// ============================== */

	const { isInTrial, trialDaysLeft, isInPredunning, invoiceableStatus, canAddPaymentDetails } =
		context;

	if (isInTrial) {
		let appearance: ButtonContent['appearance'] = 'default';
		let icon: ButtonContent['icon'] = 'gem';

		const daysLeftText = formatMessage(
			{
				id: '@atlassian/edition-awareness.button.collection.daysLeft',
				defaultMessage: '{trialDaysLeft, plural, one {# day} other {# days}} left',
				description: 'Informs the user how many days they have left in trial',
			},
			{ trialDaysLeft },
		);

		if (
			trialDaysLeft &&
			trialDaysLeft <= 3 &&
			invoiceableStatus === 'NOT_INVOICEABLE' &&
			canAddPaymentDetails
		) {
			appearance = 'warning';
			icon = 'warning';
		}
		return {
			text: daysLeftText,
			appearance,
			icon,
		};
	}

	if (isInPredunning) {
		if (canAddPaymentDetails && invoiceableStatus === 'NOT_INVOICEABLE') {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.collection.addPaymentDetails',
					defaultMessage: 'Add payment details',
				}),
				appearance: 'default',
				icon: 'gem',
			};
		}
	}

	return {
		text: formatMessage({
			id: '@atlassian/edition-awareness.button.collection.upgrade',
			defaultMessage: 'Upgrade',
		}),
		appearance: 'default',
		icon: 'gem',
	};
}

export function useButtonContent(): ButtonContent | null {
	const { formatMessage } = useIntl();

	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	const { edition, isInPredunning, isInTrial, isInCollection, product } = context;

	/*
	 * Keep this before the other checks to avoid showing the collection content in the other cases
	 */
	if (isInCollection && isProductOneOf(product, ['jira', 'confluence'])) {
		return getCollectionContent({ context, formatMessage });
	}

	if (edition === 'free') {
		return getFreeContent({ context, formatMessage, getExperimentValue });
	}

	if (isInTrial) {
		return getTrialContent({
			context,
			formatMessage,
			getExperimentValue,
		});
	}

	if (isInPredunning) {
		return getPredunningContent({
			context,
			formatMessage,
			getExperimentValue,
		});
	}

	if (isEligibleForServCoUplift(context)) {
		return {
			text: formatMessage({
				id: '@atlassian/edition-awareness.button.try-service-collection',
				defaultMessage: 'Try Service Collection',
				description: 'CTA for JSM users to uplift to Service Collection',
			}),
			appearance: 'default',
			icon: 'gem',
		};
	}

	if (isEligibleForStandardToPremium(context)) {
		return {
			text: formatMessage({
				id: '@atlassian/edition-awareness.button.upgrade.standard-to-premium',
				defaultMessage: 'Upgrade',
				description: 'Informs the user that they can upgrade their product',
			}),
			appearance: 'default',
			icon: 'gem',
			isDismissible: true,
		};
	}

	return null;
}
